import get from "lodash/get";
import moment from "moment";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Avatar from "../../../../parts/avatar";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import { groupExercisesByComplex } from "../../../../utils/complex";
import { getPlanTrainingCardContent } from "../../../../utils/plan-training-helpers";
import {
  buildSupersets,
  computeSectionPrefixes,
  groupExercisesBySection,
} from "../../../../utils/prefix-calculator";
import { splitSections } from "../../../../utils/section-types";
import { formatVolume } from "../../../../utils/volume-helpers";
import DayEditor from "./EditMode/DayEditor";
import SectionSeparator from "./EditMode/ExerciseEditor/SectionSeparator";
import WorkoutNoteBlock from "./EditMode/ExerciseEditor/WorkoutNoteBlock";
import useDayQuickEdit from "./EditMode/useDayQuickEdit";
import WeekDayExercise from "./WeekDayExercise";

const {
  DATE_FORMATS: { DATE_DAY_FORMAT },
} = require("../../../../constants");

// Clicking the day card enters quick-edit, but not when the click lands on an
// interactive element (video link, add button, coach-note field, etc.).
const isInteractiveTarget = (target) =>
  !!target.closest(
    'a, button, input, textarea, select, [role="button"], .play-icon-wrapper'
  );

const WeekDay = ({
  date,
  notes,
  coachNotes,
  onDayClick,
  editMode = false,
  syncRowIndex,
  syncColIndex,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();
  const {
    query = {},
    activeUser,
    activeUserToRequest,
    athlete,
    coachOrganizationInformation,
    scheduledSessions = {},
    scheduledSections = {},
    dailyWorkoutSummaries = {},
    cycles = [],
    femaleCycleLogs,
    femaleCycleSettings,
    withWeight,
    fromMetric,
  } = locals;

  const units = activeUser.units || "imperial";
  const dayString = date.format(DATE_DAY_FORMAT);
  const exercises = scheduledSessions[dayString];
  const sections = scheduledSections[dayString] || [];
  const summary = dailyWorkoutSummaries[dayString];
  const planContent = useMemo(
    () => getPlanTrainingCardContent(exercises, cycles),
    [exercises, cycles]
  );
  const isToday = date.isSame(moment(), "day");
  const proxyAthleteId = query.athlete || athlete?._id || activeUser._id;

  const intensityMeasure =
    get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";
  const isRiR = intensityMeasure === "RiR";

  // Per-day quick-edit state, identical to the month's MonthDay (see hook).
  // Stable reference: `exercises || []` would create a new array every render
  // for rest days, causing useDayQuickEdit's useEffect([exercises]) to fire
  // infinitely (setTempExercises([]) → re-render → repeat).
  const stableExercises = useMemo(() => exercises || [], [exercises]);

  const quickEdit = useDayQuickEdit({
    exercises: stableExercises,
    dayString,
    locals,
    t,
  });
  const { editDayMode } = quickEdit;

  const sectionGroups = groupExercisesBySection(stableExercises, sections);
  // Per-section prefixes: superset letters reset within each section, matching
  // the month view (see NormalMode/Exercises.js).
  const sectionPrefixes = computeSectionPrefixes(stableExercises, sections);
  // The workout note renders separately, above the section/exercise cards
  // below — groupExercisesBySection/computeSectionPrefixes above already
  // exclude it internally.
  const { note } = splitSections(sections);

  // Single-day "delete all" — unschedules the whole day, mirroring the month's
  // handleRemoveMultiple (CalendarMonth.js).
  const handleRemoveMultiple = (e, singleDate = date) => {
    e.preventDefault();
    if (!confirm(t("calendarMonth.removeSelectedWorkoutsConfirm"))) return;
    const day = moment(singleDate).format(DATE_DAY_FORMAT);
    submit(
      `/actions/exercise/unschedule-workout?days=${day}&proxyAthlete=${proxyAthleteId}`,
      { method: "POST", body: { days: [day] } }
    );
  };

  const compPct = summary?.completionPct ?? 0;
  let statChipClass = "";
  if (compPct >= 95) {
    statChipClass = "ok";
  } else if (compPct > 0 && compPct < 80) {
    statChipClass = "partial";
  }

  // ---- Edit mode: reuse the month's DayEditor. ----
  // Global edit mode (editMode) shows every day as a DayEditor "preview"; clicking
  // a day flips its own quick-edit (editDayMode) on, exactly like the month view.
  if (editDayMode || editMode) {
    const editingSource = editDayMode
      ? quickEdit.tempExercises
      : stableExercises;
    const supersets = buildSupersets(editingSource);
    return (
      <div
        className={`wk-col wk-quick-edit${isToday ? " is-today" : ""}`}
        data-mw-sync-row={syncRowIndex}
        data-mw-sync-col={syncColIndex}
      >
        <DayEditor
          {...quickEdit}
          editDayMode={editDayMode}
          date={date}
          dayString={dayString}
          exercises={editingSource}
          sections={sections}
          editSection={query.editSection}
          supersets={supersets}
          athleteId={proxyAthleteId}
          athlete={athlete}
          activeUser={activeUser}
          activeUserToRequest={activeUserToRequest}
          repMaximums={athlete?.repMaximums}
          withWeight={withWeight}
          fromMetric={fromMetric}
          intensityMeasure={intensityMeasure}
          isRiR={isRiR}
          scheduledSessions={scheduledSessions}
          query={query}
          femaleCycleLogs={femaleCycleLogs}
          femaleCycleSettings={femaleCycleSettings}
          handleRemoveMultiple={handleRemoveMultiple}
          opacity={false}
        />
      </div>
    );
  }

  // Read-only (edit mode off): clicking a day opens the day modal.
  const handleViewClick = (e) => {
    if (isInteractiveTarget(e.target)) return;
    if (onDayClick) onDayClick(date);
  };

  // Clicking the header navigates to the full calendar day view (see
  // calendar-day.controller.js), unlike the rest of the card which opens the
  // in-page day modal. stopPropagation keeps the card's own onClick (the
  // modal) from also firing.
  const handleHeaderClick = (e) => {
    if (isInteractiveTarget(e.target)) return;
    e.stopPropagation();
    submit("/actions/refresh/?", {
      method: "GET",
      body: {
        view: "day",
        date: dayString,
        month: "",
        week: "",
        relTrends: true,
      },
    });
  };

  // ---- Rest / empty day (read-only; click opens the day modal) ----
  // A day with a section but no exercises yet (e.g. "Game Day" added ahead of
  // programming) still has content to show, so it falls through to the full
  // card below instead of being treated as a rest day.
  if (!exercises?.length && !sections.length) {
    return (
      <div
        className={`wk-col is-rest${isToday ? " is-today" : ""}`}
        data-mw-sync-row={syncRowIndex}
        data-mw-sync-col={syncColIndex}
      >
        {/* biome-ignore lint/a11y/noStaticElementInteractions: read-only day opens the modal on click */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: inner links handle their own keyboard interaction */}
        <div className="wk-col-card" onClick={handleViewClick}>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: header navigates to the day view on click */}
          <header className="day-header" onClick={handleHeaderClick}>
            <div className="day-header-left">
              <span className="day-name">{date.format("ddd")}</span>
              <span className="day-number">{date.format("D")}</span>
            </div>
            {isToday && (
              <div className="day-header-right">
                <span className="wk-day-today-badge">
                  {t("calendar.today", "Today")}
                </span>
              </div>
            )}
          </header>
          <div className="wk-rest-card">
            <span className="wk-rest-emoji" aria-hidden="true">
              🛏️
            </span>
            <span>{t("calendar.rest", "REST")}</span>
          </div>
          {coachNotes?.note && (
            <div className="wk-coach-note">
              <Avatar
                className="wk-coach-avatar"
                user={coachOrganizationInformation}
                size={20}
              />
              <span>{coachNotes.note}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---- View mode: actuals review (Log training-card styling). ----
  return (
    <div
      className={`wk-col${isToday ? " is-today" : ""}`}
      data-mw-sync-row={syncRowIndex}
      data-mw-sync-col={syncColIndex}
    >
      <div className="wk-col-card">
        {/* biome-ignore lint/a11y/noStaticElementInteractions: header navigates to the day view on click */}
        <header className="day-header" onClick={handleHeaderClick}>
          <div className="day-header-left">
            <span className="day-name">{date.format("ddd")}</span>
            <span className="day-number">{date.format("D")}</span>
          </div>
          {(isToday || summary?.totalVolume > 0 || summary) && (
            <div className="day-header-right">
              {isToday && (
                <span className="wk-day-today-badge">
                  {t("calendar.today", "Today")}
                </span>
              )}
              {summary?.totalVolume > 0 && (
                <span className="wk-vol-badge">
                  {formatVolume(summary.totalVolume, units)}
                </span>
              )}
              {summary && (
                <span className={`wk-comp-badge ${statChipClass}`}>
                  {summary.completedExercises}/{summary.plannedExercises}
                </span>
              )}
            </div>
          )}
        </header>

        {/* Always rendered (even with no plan text) so wk-plan-meta's reserved
            height keeps every day's exercise list starting at the same row. */}
        <div className="wk-plan-meta">
          {planContent.workoutName && (
            <div className="wk-workout-name">{planContent.workoutName}</div>
          )}
          {planContent.programName && (
            <div className="wk-program-meta clipped">
              {planContent.programName}
            </div>
          )}
          {planContent.blockName && (
            <div className="wk-block-meta">{planContent.blockName}</div>
          )}
        </div>

        {/* biome-ignore lint/a11y/noStaticElementInteractions: read-only day opens the modal on click */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: inner links/buttons handle their own keyboard interaction */}
        <div className="day-cards" onClick={handleViewClick}>
          <WorkoutNoteBlock note={note} editable={false} collapsible />
          {sectionGroups.map(({ section, exercises: secExs }) => {
            if (!secExs.length && !section) return null;
            return (
              <Fragment key={section?._id || "unsectioned"}>
                {section && (
                  <SectionSeparator
                    section={section}
                    athleteId={proxyAthleteId}
                    reqSource="calendar"
                    editable={false}
                    nameEditable={true}
                    showNameLabel={false}
                    dragHandleClass=""
                  />
                )}
                {groupExercisesByComplex(secExs).map((ex) => (
                  <WeekDayExercise
                    key={ex._id}
                    exercise={ex}
                    exercises={ex.exercises}
                    prefix={sectionPrefixes[ex._id]}
                  />
                ))}
              </Fragment>
            );
          })}

          {notes?.note && (
            <div className="wk-day-note">
              <Avatar className="wk-day-note-avatar" user={athlete} size={20} />
              <span>{notes.note}</span>
            </div>
          )}

          {coachNotes?.note && (
            <div className="wk-coach-note mt-1">
              <Avatar
                className="wk-coach-avatar"
                user={coachOrganizationInformation}
                size={20}
              />
              <span>{coachNotes.note}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeekDay;
